/* custom-rebuy-carousel.js — theme-owned controls for Rebuy's Splide product carousels (step 5 of the
   Rebuy widgets handover, Claude Design 2026-10-08: docs/imports/2026-10-08-rebuy-widgets-reply-from-design.md
   sections 2 and 3, board option 5c). One control group per carousel, inserted by this script right
   after Rebuy's .rebuy-product-grid inside .rebuy-widget-content; assets/custom-rebuy-widgets.css
   (the STEP 5 block) lays it out and hides Rebuy's own arrows and dots:

     div.wg-carousel__controls[role=group]
       button.wg-carousel__prev[aria-label][aria-controls=<track id>]   (desktop only, by CSS)
       div.wg-carousel__bar[aria-hidden=true] > span.wg-carousel__bar-fill
       button.wg-carousel__next[aria-label][aria-controls=<track id>]

   WHAT IT DOES
   - Arrows: the theme buttons click Rebuy's own Splide arrows (button.splide__arrow--prev / --next,
     display:none by CSS; HTMLElement.click() runs the handler on a hidden or inert button, verified
     on the dev theme 2026-10-08). Rebuy initialises Splide with rewind:true and exposes no setting for
     it, so the ends are imposed here: prev is disabled while the first card is fully inside the
     track, next while the last card is, and a click at an end is dropped, so Splide's rewind never
     runs from these controls (drag and swipe do not rewind: Splide's rewindByDrag is off). Each click
     moves what Rebuy configured, a page of cards (Rebuy has no per-move setting).
   - Progress bar: fill width = list width / full row width (the share of cards in view; 16px floor),
     fill offset = list offset / (row width - list width). Updated on every move, every drag frame,
     every Rebuy refresh and on resize; the 200 ms transition is CSS, none under reduced motion.
   - Visibility: CSS shows the group only while the grid carries Splide's `is-overflow`
     (.splide.is-overflow + .wg-carousel__controls). Rebuy destroys Splide when a carousel has no more
     cards than columns and the class goes with it, so the group is gone there and on an empty widget.
   - Nudge (the mobile scroll hint): once per page load, the first carousel that is 60% in view
     (IntersectionObserver), still at its start, overflowing and untouched gets the class
     wg-carousel-nudge on its .splide__track for one CSS keyframe (40px left and back, 900 ms);
     never under prefers-reduced-motion, never after a pointer or touch on that carousel, and only
     below the arrows' breakpoint (990px): the hint exists because phones have no arrows (handover
     README section 5, "Scroll hint on mobile"). The track moves, never .splide__list, which Splide
     positions by its own transform.

   POSITION SOURCE: the geometry Splide itself uses, read from the DOM. Splide writes the destination
   `transform: translateX(-Npx)` on .splide__list at the start of every move and on every frame of a
   drag; a slide's static offset in the row is its rect minus the list's rect (both carry the
   transform); the row width is the last slide's right edge minus the list's left (Splide's
   sliderSize(true)); the visible width is the list's rect width (Splide's listSize()). Why not the
   slides' is-visible / aria-hidden classes: measured on the dev theme 2026-10-08, Splide's slide
   state classes are absent at rest and after every refresh Rebuy triggers (restoreCarouselProducts
   re-adds the slides), until the first move. Why not the Splide instance: Rebuy keeps one at
   window.Rebuy.widgets[i].View.carousel, but that path is Rebuy's internal and moves with its
   builds; the class contract below is Splide's public CSS API. A MutationObserver on the grid
   (class and style attributes, child lists) is the trigger; one update per animation frame.

   DEPENDENCIES ON REBUY'S AND SPLIDE'S MARKUP (the grep after a Rebuy update; everything is read,
   nothing of Rebuy's is rewritten): the scope .rebuy-widget.widget-type-product.widget-display-embed
   .widget-layout-grid outside #rebuy-cart (the same "G" as assets/custom-rebuy-widgets.css);
   .rebuy-product-grid as Splide's root with the classes splide and is-overflow; .splide__track (its
   id "<root id>-track" is the buttons' aria-controls target), .splide__list and its inline
   transform, .splide__slide (Rebuy's .rebuy-product-block; clones excluded), button.splide__arrow--prev
   / --next; Rebuy's document events rebuy.ready, rebuy.productsChange, rebuy.show, rebuy.loaded and
   rebuy.refresh (each triggers a re-scan; a MutationObserver on <body> covers everything else).
   Runtime checks on a product page of the dev theme:
     document.querySelectorAll('.wg-carousel__controls').length        // one per carousel in scope
     document.querySelector('.rebuy-product-grid.splide .splide__list').style.transform  // "translateX(-Npx)" after a move
     Rebuy.widgets.find(function (w) { return w.id == '41134'; }).View.carousel.options.rewind
       // true: Rebuy's default; the ends are imposed by this script, not by Splide

   Loaded deferred from layout/theme.liquid after custom-buttons.js, on every page (the carousels are
   on product pages and the homepage; elsewhere the script finds nothing and idles). No dependencies;
   ES5 syntax; MutationObserver, WeakMap, closest and requestAnimationFrame are required (otherwise the
   script exits and Rebuy's own arrows and dots stay hidden by CSS, so the row still swipes);
   IntersectionObserver and ResizeObserver are optional. */
(function () {
  'use strict';

  if (!window.MutationObserver || !window.WeakMap || !window.requestAnimationFrame ||
      !window.Element || !Element.prototype.closest || !document.body) {
    return;
  }

  var SCOPE = '.rebuy-widget.widget-type-product.widget-display-embed.widget-layout-grid';
  var CART = '#rebuy-cart';
  var GRID = '.rebuy-product-grid';
  var GROUP_CLASS = 'wg-carousel__controls';
  var DRAGGING_CLASS = 'is-dragging';      /* on the group while a pointer is down on the row: the bar follows without its transition */
  var NUDGE_CLASS = 'wg-carousel-nudge';   /* on .splide__track; the keyframe lives in custom-rebuy-widgets.css */
  var ARROWS_MEDIA = '(min-width: 990px)'; /* mirrors the CSS: arrows above it, the nudge below it */
  var NUDGE_ONCE_PER_PAGE = true;          /* the reply, section 2: "once per page load"; false = once per carousel (the board's WeakSet) */
  var NUDGE_THRESHOLD = 0.6;               /* share of the row in view before the hint (the reply, section 2) */
  var NUDGE_DELAY_MS = 300;                /* the board's pause between entering view and the move */
  var NUDGE_MAX_MS = 1500;                 /* the class comes off at animationend, or here if no animation ran */
  var SCAN_DELAY_MS = 50;                  /* coalesces Rebuy's render bursts into one scan */
  var MIN_FILL_PX = 16;                    /* the fill's floor (the reply, section 3) */
  var EDGE_PX = 1;                         /* sub-pixel tolerance on "fully inside the track" */
  var LABEL_PREV = 'Previous products';
  var LABEL_NEXT = 'Next products';
  var LABEL_GROUP = 'Carousel controls';
  var CHEVRON_PREV = 'M12 2L2 12l10 10';   /* the board's 8 × 14 chevrons: viewBox 0 0 14 24, stroke 2, round caps */
  var CHEVRON_NEXT = 'M2 2l10 10L2 22';
  var RESCAN_EVENTS = ['rebuy.ready', 'rebuy.productsChange', 'rebuy.show', 'rebuy.loaded', 'rebuy.refresh'];

  var byGrid = new WeakMap();   /* Rebuy's grid element -> controller */
  var byGroup = new WeakMap();  /* the theme's group element -> controller */
  var live = [];                /* controllers with a grid, for the resize handler */
  var pageNudged = false;
  var scanTimer = 0;
  var reduceMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var arrowsMedia = window.matchMedia ? window.matchMedia(ARROWS_MEDIA) : null;

  function prefersReducedMotion() { return !!(reduceMotion && reduceMotion.matches); }
  function arrowsShown() { return !!(arrowsMedia && arrowsMedia.matches); }
  function connected(el) {
    return !!el && (typeof el.isConnected === 'boolean' ? el.isConnected : document.body.contains(el));
  }

  /* ---------- the control group ---------- */

  function chevron(d) {
    var ns = 'http://www.w3.org/2000/svg';
    var svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('width', '8');
    svg.setAttribute('height', '14');
    svg.setAttribute('viewBox', '0 0 14 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    var path = document.createElementNS(ns, 'path');
    path.setAttribute('d', d);
    /* the board's 14 x 24 drawing is shown at 8 x 14, which would thin the stroke to about 1.1px;
       the design reply asks for a 2px stroke, so the stroke is kept at screen scale */
    path.setAttribute('vector-effect', 'non-scaling-stroke');
    svg.appendChild(path);
    return svg;
  }

  function arrowButton(className, label, d) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = className;
    btn.setAttribute('aria-label', label);
    btn.appendChild(chevron(d));
    return btn;
  }

  function createController() {
    var group = document.createElement('div');
    group.className = GROUP_CLASS;
    /* no role or name on the wrapper: on phones the buttons are hidden and the bar is decorative,
       so a named group would announce as empty; the two buttons carry their own labels */
    var prev = arrowButton('wg-carousel__prev', LABEL_PREV, CHEVRON_PREV);
    var bar = document.createElement('div');
    bar.className = 'wg-carousel__bar';
    bar.setAttribute('aria-hidden', 'true');
    var fill = document.createElement('span');
    fill.className = 'wg-carousel__bar-fill';
    bar.appendChild(fill);
    var next = arrowButton('wg-carousel__next', LABEL_NEXT, CHEVRON_NEXT);
    group.appendChild(prev);
    group.appendChild(bar);
    group.appendChild(next);

    var ctrl = {
      group: group, prev: prev, bar: bar, fill: fill, next: next,
      grid: null, mo: null, ro: null, io: null, raf: 0,
      nudgeTimer: 0, nudged: false, touched: false, touchX: null, touchY: null
    };

    prev.addEventListener('click', function () { step(ctrl, -1); });
    next.addEventListener('click', function () { step(ctrl, 1); });

    ctrl.onMutation = function () { schedule(ctrl); };
    ctrl.onPointerUp = function () {
      window.removeEventListener('pointerup', ctrl.onPointerUp);
      window.removeEventListener('pointercancel', ctrl.onPointerUp);
      group.classList.remove(DRAGGING_CLASS);
      schedule(ctrl);
    };
    ctrl.onPointerDown = function (event) {
      /* a mouse or pen on the row counts as touched; a finger is judged by its direction in
         onTouchMove, because a vertical page scroll often starts on the row (the owner's phone,
         2026-10-08: the hint never played) */
      if (!event || event.pointerType !== 'touch') touched(ctrl);
      group.classList.add(DRAGGING_CLASS);
      window.addEventListener('pointerup', ctrl.onPointerUp);
      window.addEventListener('pointercancel', ctrl.onPointerUp);
    };
    ctrl.onTouchStart = function (event) {
      var t = event && event.touches && event.touches[0];
      ctrl.touchX = t ? t.clientX : null;
      ctrl.touchY = t ? t.clientY : null;
    };
    ctrl.onTouchMove = function (event) {
      if (ctrl.touchX == null) return;
      var t = event && event.touches && event.touches[0];
      if (!t) return;
      var dx = Math.abs(t.clientX - ctrl.touchX);
      var dy = Math.abs(t.clientY - ctrl.touchY);
      if (dx < 8 && dy < 8) return;
      ctrl.touchX = null;              /* decided for this touch */
      if (dx > dy) touched(ctrl);      /* a horizontal swipe on the row */
    };

    byGroup.set(group, ctrl);
    return ctrl;
  }

  /* ---------- geometry (what Splide's Slide.isVisible() and Move.getLimit() compute) ---------- */

  function listOffset(list) {
    var m = /translate(?:X|3d)?\(\s*(-?[\d.]+)px/.exec(list.style.transform || '');
    return m ? -parseFloat(m[1]) : 0;
  }

  function measure(grid) {
    var track = grid.querySelector('.splide__track');
    var list = track ? track.querySelector('.splide__list') : null;
    if (!list) return null;
    var slides = list.querySelectorAll('.splide__slide:not(.splide__slide--clone)');
    if (!slides.length) return null;
    var listRect = list.getBoundingClientRect();
    var lastRect = slides[slides.length - 1].getBoundingClientRect();
    var listSize = listRect.width;
    var sliderSize = lastRect.right - listRect.left;
    var offset = listOffset(list);
    var limit = Math.max(0, sliderSize - listSize);
    return {
      track: track,
      overflow: limit > EDGE_PX,
      atStart: offset <= EDGE_PX,
      atEnd: offset >= limit - EDGE_PX,
      ratio: sliderSize > 0 ? Math.min(1, listSize / sliderSize) : 1,
      progress: limit > 0 ? Math.min(1, Math.max(0, offset / limit)) : 0
    };
  }

  /* ---------- state ---------- */

  function setDisabled(btn, disabled, other) {
    if (btn.disabled === disabled) return;
    /* keyboard: a button that disables under focus would drop focus to <body>; hand it across */
    if (disabled && document.activeElement === btn && other && !other.disabled) other.focus();
    btn.disabled = disabled;
  }

  function update(ctrl) {
    ctrl.raf = 0;
    var grid = ctrl.grid;
    if (!grid || !connected(grid)) return;
    var m = measure(grid);
    if (!m) {
      setDisabled(ctrl.prev, true);
      setDisabled(ctrl.next, true);
      ctrl.group.hidden = true;
      return;
    }
    /* Splide keeps is-overflow on a root it has destroyed at a breakpoint (Rebuy destroys it when
       the cards fit the columns), so the CSS gate on that class is not enough: the group is hidden
       whenever the carousel is not live or the row does not overflow its track */
    ctrl.group.hidden = !grid.classList.contains('is-active') || !m.overflow;
    if (m.track.id) {
      ctrl.prev.setAttribute('aria-controls', m.track.id);
      ctrl.next.setAttribute('aria-controls', m.track.id);
    }
    setDisabled(ctrl.prev, m.atStart, ctrl.next);
    setDisabled(ctrl.next, m.atEnd, ctrl.prev);
    var barWidth = ctrl.bar.clientWidth; /* 80 / 160 from the CSS; 0 while the group is display:none */
    if (barWidth > 0) {
      var fillWidth = Math.min(barWidth, Math.max(MIN_FILL_PX, Math.round(m.ratio * barWidth)));
      var x = Math.round(m.progress * (barWidth - fillWidth));
      ctrl.fill.style.width = fillWidth + 'px';
      ctrl.fill.style.transform = 'translateX(' + x + 'px)';
    }
  }

  function schedule(ctrl) {
    if (ctrl.raf) return;
    ctrl.raf = requestAnimationFrame(function () {
      clearTimeout(ctrl.rafFallback);
      ctrl.rafFallback = 0;
      update(ctrl);
    });
    /* requestAnimationFrame pauses in background tabs and in some embedded views (measured in the
       desktop app's browser pane); a timer keeps the end states and the bar current there too */
    ctrl.rafFallback = setTimeout(function () {
      ctrl.rafFallback = 0;
      if (ctrl.raf) {
        cancelAnimationFrame(ctrl.raf);
        update(ctrl);
      }
    }, 120);
  }

  /* the proxy click: measured again at click time so a stale frame can never reach Splide's rewind */
  function step(ctrl, dir) {
    var grid = ctrl.grid;
    if (!grid) return;
    var m = measure(grid);
    if (!m || (dir < 0 ? m.atStart : m.atEnd)) return;
    var arrow = grid.querySelector(dir < 0 ? '.splide__arrow--prev' : '.splide__arrow--next');
    if (arrow) arrow.click();
    ctrl.touched = true;
  }

  /* ---------- the nudge ---------- */

  function touched(ctrl) {
    ctrl.touched = true;
    cancelNudge(ctrl);
  }

  function cancelNudge(ctrl) {
    if (ctrl.nudgeTimer) {
      clearTimeout(ctrl.nudgeTimer);
      ctrl.nudgeTimer = 0;
    }
  }

  function nudgeDone() {
    return NUDGE_ONCE_PER_PAGE ? pageNudged : false;
  }

  function stopWatchingNudge(ctrl) {
    cancelNudge(ctrl);
    if (ctrl.io) {
      ctrl.io.disconnect();
      ctrl.io = null;
    }
  }

  function watchNudge(ctrl) {
    if (!window.IntersectionObserver || prefersReducedMotion() || ctrl.nudged || nudgeDone()) return;
    ctrl.io = new IntersectionObserver(function (entries) {
      var inView = false;
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting && entries[i].intersectionRatio >= NUDGE_THRESHOLD) inView = true;
      }
      if (!inView) {
        cancelNudge(ctrl); /* left view before the pause ended */
      } else if (!ctrl.nudgeTimer) {
        ctrl.nudgeTimer = setTimeout(function () {
          ctrl.nudgeTimer = 0;
          runNudge(ctrl);
        }, NUDGE_DELAY_MS);
      }
    }, { threshold: NUDGE_THRESHOLD });
    ctrl.io.observe(ctrl.grid);
  }

  function runNudge(ctrl) {
    var grid = ctrl.grid;
    if (!grid || !connected(grid)) return;
    if (ctrl.nudged || nudgeDone()) { stopWatchingNudge(ctrl); return; }
    if (ctrl.touched || prefersReducedMotion()) { stopWatchingNudge(ctrl); return; }
    if (arrowsShown()) return;                           /* desktop has arrows; keep watching in case the viewport narrows */
    if (!grid.classList.contains('is-active')) return;   /* Splide destroyed at this width: a static row, nothing to hint at */
    if (!grid.classList.contains('is-overflow')) return; /* nothing to hint at; keep watching: Rebuy may still be adding cards */
    var m = measure(grid);
    if (!m) return;                                      /* slides not in the list yet; keep watching */
    if (!m.atStart) { stopWatchingNudge(ctrl); return; } /* already moved by the user */
    var track = m.track;
    if (track.classList.contains(NUDGE_CLASS)) return;
    ctrl.nudged = true;
    pageNudged = true;
    stopWatchingNudge(ctrl);
    var timer = 0;
    var done = function (event) {
      if (event && (event.target !== track || event.animationName !== NUDGE_CLASS)) return;
      clearTimeout(timer);
      track.removeEventListener('animationend', done);
      track.removeEventListener('animationcancel', done);
      track.classList.remove(NUDGE_CLASS);
    };
    track.addEventListener('animationend', done);
    track.addEventListener('animationcancel', done);
    timer = setTimeout(done, NUDGE_MAX_MS);
    track.classList.add(NUDGE_CLASS);
  }

  /* ---------- binding a controller to Rebuy's grid ---------- */

  function detach(ctrl) {
    var grid = ctrl.grid;
    if (!grid) return;
    stopWatchingNudge(ctrl);
    if (ctrl.mo) ctrl.mo.disconnect();
    if (ctrl.ro) ctrl.ro.disconnect();
    ctrl.mo = ctrl.ro = null;
    if (ctrl.raf) {
      cancelAnimationFrame(ctrl.raf);
      ctrl.raf = 0;
    }
    if (ctrl.rafFallback) {
      clearTimeout(ctrl.rafFallback);
      ctrl.rafFallback = 0;
    }
    grid.removeEventListener('pointerdown', ctrl.onPointerDown, true);
    grid.removeEventListener('touchstart', ctrl.onTouchStart, true);
    grid.removeEventListener('touchmove', ctrl.onTouchMove, true);
    if (byGrid.get(grid) === ctrl) byGrid['delete'](grid);
    var i = live.indexOf(ctrl);
    if (i !== -1) live.splice(i, 1);
    ctrl.grid = null;
  }

  function attach(ctrl, grid) {
    detach(ctrl);
    ctrl.grid = grid;
    byGrid.set(grid, ctrl);
    live.push(ctrl);
    ctrl.mo = new MutationObserver(ctrl.onMutation);
    ctrl.mo.observe(grid, { attributes: true, attributeFilter: ['class', 'style'], childList: true, subtree: true });
    if (window.ResizeObserver) {
      ctrl.ro = new ResizeObserver(ctrl.onMutation);
      ctrl.ro.observe(grid);
    }
    /* capture + passive: Splide's own drag listeners on the track are untouched, and no scroll is blocked */
    grid.addEventListener('pointerdown', ctrl.onPointerDown, { capture: true, passive: true });
    grid.addEventListener('touchstart', ctrl.onTouchStart, { capture: true, passive: true });
    grid.addEventListener('touchmove', ctrl.onTouchMove, { capture: true, passive: true });
    watchNudge(ctrl);
    schedule(ctrl);
  }

  /* the group that already sits among the grid's siblings, if any (Rebuy's Vue may re-render the
     grid element itself; the theme's group is not Vue's and stays behind) */
  function siblingGroup(grid) {
    var parent = grid.parentNode;
    if (!parent) return null;
    for (var i = 0; i < parent.children.length; i++) {
      if (parent.children[i].classList.contains(GROUP_CLASS)) return parent.children[i];
    }
    return null;
  }

  function ensure(grid) {
    var parent = grid.parentNode;
    if (!parent) return;
    var ctrl = byGrid.get(grid);
    if (!ctrl) {
      var orphan = siblingGroup(grid);
      if (orphan) {
        ctrl = byGroup.get(orphan) || null;
        if (ctrl && ctrl.grid && ctrl.grid !== grid && connected(ctrl.grid)) ctrl = null; /* another live grid's group */
        if (!ctrl) parent.removeChild(orphan);                                            /* not ours to reuse */
      }
      if (!ctrl) ctrl = createController();
      attach(ctrl, grid);
    }
    if (grid.nextElementSibling !== ctrl.group) parent.insertBefore(ctrl.group, grid.nextSibling);
  }

  function scan() {
    scanTimer = 0;
    /* controllers whose grid left the document (Rebuy re-rendered a widget whole) are released
       first, so their observers and the resize handler stop working on a detached subtree */
    for (var k = live.length - 1; k >= 0; k--) {
      if (live[k].grid && !connected(live[k].grid)) {
        var gone = live[k];
        detach(gone);
        if (gone.group && gone.group.parentNode) gone.group.parentNode.removeChild(gone.group);
      }
    }
    var grids = document.querySelectorAll(SCOPE + ' ' + GRID);
    for (var i = 0; i < grids.length; i++) {
      if (grids[i].closest(CART)) continue;
      ensure(grids[i]);
    }
  }

  function queueScan() {
    if (!scanTimer) scanTimer = setTimeout(scan, SCAN_DELAY_MS);
  }

  /* ---------- boot ---------- */

  new MutationObserver(function (records) {
    for (var i = 0; i < records.length; i++) {
      var added = records[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        if (added[j].nodeType === 1) {
          queueScan();
          return;
        }
      }
    }
  }).observe(document.body, { childList: true, subtree: true });

  for (var e = 0; e < RESCAN_EVENTS.length; e++) {
    document.addEventListener(RESCAN_EVENTS[e], queueScan);
  }

  window.addEventListener('resize', function () {
    for (var i = 0; i < live.length; i++) schedule(live[i]);
  });

  scan();
})();
